import React from 'react'
import { Alert, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useSettingsIntegrations } from '../Settings'

const cards = [
  {
    id: 'email',
    title: 'Email (IMAP)',
    description: 'Connect an IMAP inbox to receive listener emails directly inside the unified inbox.',
    link: '/settings/integrations/email',
  },
  {
    id: 'telegram',
    title: 'Telegram',
    description: 'Ingest Telegram bot messages in real time via webhook.',
    link: '/settings/integrations/telegram',
  },
] as const

const IntegrationsHome: React.FC = () => {
  const { stationId, providers, loading, error } = useSettingsIntegrations()

  if (!stationId) {
    return <Alert severity="info">Select a station to manage integrations.</Alert>
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h5" component="h2">
          Integrations
        </Typography>
        <Typography color="text.secondary">
          Connect your station&apos;s messaging channels to deliver audience messages directly into the unified inbox.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Stack spacing={2.5}>
        {cards.map((card) => {
          const provider = providers[card.id]

          let statusChip: React.ReactNode = null

          if (!provider) {
            statusChip = loading ? <Chip label="Loading…" size="small" /> : <Chip label="Unavailable" size="small" />
          } else {
            switch (provider.status) {
              case 'ok':
                statusChip = <Chip label="Connected" size="small" color="success" />
                break
              case 'corrupt':
                statusChip = <Chip label="Error" size="small" color="error" variant="outlined" />
                break
              default:
                statusChip = <Chip label="Not configured" size="small" color="default" variant="outlined" />
                break
            }
          }

          const providerError = provider?.status === 'corrupt' && provider?.error ? String(provider.error) : null
          const showSkeleton = loading && !provider

          return (
            <Paper key={card.id} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                  <Stack spacing={0.5}>
                    <Typography variant="h6">{card.title}</Typography>
                    <Typography color="text.secondary">{card.description}</Typography>
                  </Stack>
                  {showSkeleton ? <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: 12 }} /> : statusChip}
                </Stack>

                {providerError ? <Alert severity="error">{providerError}</Alert> : null}

                <Stack direction="row" justifyContent="flex-start">
                  <Button component={RouterLink} to={card.link} variant="contained">
                    Configure
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default IntegrationsHome
